import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, apiCall } from '@/AuthContext';
import { motion } from 'framer-motion';
import { Shield, LogOut, BookOpen, Calendar, CreditCard, User, CheckCircle, XCircle, Clock, AlertCircle, Printer, Download, FileDown, Share2, Target, Trophy, Sparkles, ChevronRight, Lock, FileText, Brain, Gift, Copy, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { downloadReceiptPDF, shareReceiptPDF } from '@/utils/receiptPdf';

export default function StudentDashboard() {
  const { user, logout } = useAuth();
  const [student, setStudent] = useState(null);
  const [fees, setFees] = useState({ fees: [], total_due: 0, total_paid: 0 });
  const [attendance, setAttendance] = useState({ records: [], total_days: 0, present_days: 0, late_days: 0, absent_days: 0, leave_days: 0, percentage: 0 });
  const [tab, setTab] = useState('overview');
  const [showReceipt, setShowReceipt] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const receiptRef = useRef(null);
  // Quiz state
  const [todayQuiz, setTodayQuiz] = useState(null);
  const [myAttempts, setMyAttempts] = useState([]);
  const [quizStage, setQuizStage] = useState('intro'); // intro | quiz | result
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizCurrentQ, setQuizCurrentQ] = useState(0);
  const [quizResult, setQuizResult] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [plansInfo, setPlansInfo] = useState(null);
  const [payForm, setPayForm] = useState({ plan: 'basic', utr_number: '', months: 1, notes: '' });
  // Notes
  const [notes, setNotes] = useState([]);
  const [openNote, setOpenNote] = useState(null);
  // 100-Day Plan
  const [studyPlan, setStudyPlan] = useState(null);
  // Referrals
  const [referrals, setReferrals] = useState(null);
  // AI Analysis
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  // Razorpay
  const [rzpLoading, setRzpLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user?.student_id) return;
    const calls = await Promise.allSettled([
      apiCall('get', `/students/${user.student_id}`),
      apiCall('get', `/fees/student/${user.student_id}`),
      apiCall('get', `/attendance/student/${user.student_id}`),
      apiCall('get', '/quizzes/today'),
      apiCall('get', '/quiz-attempts/me'),
      apiCall('get', '/online/my-subscription'),
      apiCall('get', '/online/plans'),
      apiCall('get', '/notes'),
      apiCall('get', '/online/study-plan'),
      apiCall('get', '/referrals/me'),
    ]);
    if (calls[0].status === 'fulfilled') setStudent(calls[0].value.data);
    if (calls[1].status === 'fulfilled') setFees(calls[1].value.data);
    if (calls[2].status === 'fulfilled') setAttendance(calls[2].value.data);
    if (calls[3].status === 'fulfilled' && calls[3].value.data) {
      setTodayQuiz(calls[3].value.data);
      setQuizAnswers(new Array(calls[3].value.data.total_questions).fill(null));
    }
    if (calls[4].status === 'fulfilled') setMyAttempts(calls[4].value.data || []);
    if (calls[5].status === 'fulfilled') {
      setSubscription(calls[5].value.data);
      if (calls[5].value.data?.plan) setPayForm(p => ({ ...p, plan: calls[5].value.data.plan }));
    }
    if (calls[6].status === 'fulfilled') setPlansInfo(calls[6].value.data);
    if (calls[7].status === 'fulfilled') setNotes(calls[7].value.data || []);
    if (calls[8].status === 'fulfilled') setStudyPlan(calls[8].value.data);
    if (calls[9].status === 'fulfilled') setReferrals(calls[9].value.data);
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const viewReceipt = async (fee) => {
    try {
      const res = await apiCall('get', `/fees/receipt/${user.student_id}/${fee.month}/${fee.year}`);
      setReceipt(res.data);
      setShowReceipt(true);
    } catch {
      toast.error('Receipt not available');
    }
  };

  const handleDownloadPDF = () => {
    if (receipt) {
      downloadReceiptPDF(receipt);
      toast.success('Receipt PDF downloaded');
    }
  };

  const handleSharePDF = () => {
    if (receipt) {
      shareReceiptPDF(receipt);
      toast.success('Sharing receipt...');
    }
  };

  const isOnline = student?.is_online || student?.batch_id === 'online' || !!subscription;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    ...(isOnline ? [] : [{ id: 'fees', label: 'Fees', icon: CreditCard }]),
    ...(isOnline ? [] : [{ id: 'attendance', label: 'Attendance', icon: Calendar }]),
    { id: 'mocktest', label: 'Daily Mock Test', icon: Target },
    ...(isOnline ? [{ id: 'plan100', label: '100-Day Plan', icon: Layers }] : []),
    { id: 'notes', label: 'Study Notes', icon: BookOpen },
    ...(isOnline ? [{ id: 'referrals', label: 'Refer & Earn', icon: Gift }] : []),
    ...(subscription ? [{ id: 'myplan', label: 'My Plan', icon: Sparkles }] : []),
  ];

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!payForm.utr_number.trim()) { toast.error('UTR / transaction number required'); return; }
    try {
      await apiCall('post', '/online/submit-payment', payForm);
      toast.success('Payment submitted! Admin will approve within 30 mins.');
      fetchData();
    } catch (er) { toast.error(er.response?.data?.detail || 'Failed'); }
  };

  // Load Razorpay checkout.js once
  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const payWithRazorpay = async (plan, months = 1) => {
    setRzpLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Could not load Razorpay. Check internet.'); setRzpLoading(false); return; }
      const orderRes = await apiCall('post', '/online/razorpay-create-order', { plan, months });
      const order = orderRes.data;
      if (!order.key_id) { toast.error('Razorpay not configured. Use GPay below.'); setRzpLoading(false); return; }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: 'INR',
        name: 'SIGNATURE PSC',
        description: `${plansInfo?.plans?.[plan]?.name || plan} (${months} month${months > 1 ? 's' : ''})`,
        order_id: order.order_id,
        prefill: { name: student?.name || '', email: user?.email || '', contact: student?.phone || '' },
        theme: { color: '#d4af37' },
        handler: async (resp) => {
          try {
            await apiCall('post', '/online/razorpay-verify', resp);
            toast.success('Payment successful! Plan activated.');
            fetchData();
          } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
        },
        modal: { ondismiss: () => setRzpLoading(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start payment');
    } finally {
      setRzpLoading(false);
    }
  };

  const fetchAiAnalysis = async (attemptId) => {
    if (!attemptId) return;
    setAiLoading(true); setAiAnalysis(null);
    try {
      const r = await apiCall('post', `/quiz-attempts/${attemptId}/analyze`);
      setAiAnalysis(r.data);
    } catch (e) { toast.error('AI analysis failed'); }
    setAiLoading(false);
  };

  const copyReferralLink = () => {
    if (!referrals) return;
    navigator.clipboard.writeText(referrals.share_link).then(() => toast.success('Link copied!'));
  };
  const shareReferralWhatsApp = () => {
    if (!referrals) return;
    const msg = `Join me on SIGNATURE PSC Online — Kerala LDC 100-Day Coaching! Use my referral code *${referrals.referral_code}* for the trial. Sign up: ${referrals.share_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const submitQuiz = async () => {
    setQuizLoading(true);
    setAiAnalysis(null);
    try {
      const res = await apiCall('post', '/quiz-attempts/submit', { quiz_id: todayQuiz.id, answers: quizAnswers });
      setQuizResult(res.data); setQuizStage('result');
      const att = await apiCall('get', '/quiz-attempts/me'); setMyAttempts(att.data || []);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Auto-trigger AI analysis (fire and forget — UI shows loader)
      if (res.data?.attempt_id) fetchAiAnalysis(res.data.attempt_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    setQuizLoading(false);
  };

  const setQuizAnswer = (qIdx, optIdx) => {
    const next = [...quizAnswers]; next[qIdx] = optIdx; setQuizAnswers(next);
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="student-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-[#27272a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_signature-psc-portal/artifacts/h1jchf2q_IMG-20260126-WA0188.jpg" alt="Logo" className="w-8 h-8 rounded-md object-contain bg-white p-0.5" />
            <div>
              <span className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Student Portal</span>
              <span className="text-[10px] block text-[#a1a1aa]">{user?.name}</span>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="text-[#a1a1aa] hover:text-white hover:bg-[#27272a]" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${tab === t.id ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] hover:bg-[#27272a] border border-[#27272a]'}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard icon={CreditCard} label="Pending Dues" value={`₹${fees.total_due}`} accent={fees.total_due > 0 ? '#ef4444' : '#10b981'} />
              <StatCard icon={CreditCard} label="Total Paid" value={`₹${fees.total_paid}`} accent="#10b981" />
              <StatCard icon={Calendar} label="Attendance" value={`${attendance.percentage}%`} accent="#d4af37" />
            </div>
            {student && (
              <div className="p-6 md:p-8 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid="student-info-card">
                <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Profile Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { label: 'Student ID', value: student.student_id },
                    { label: 'Name', value: student.name },
                    { label: 'Date of Birth', value: student.dob },
                    { label: 'Phone', value: student.phone },
                    { label: 'Batch', value: student.batch_id?.replace('_', ' ').toUpperCase() },
                    { label: 'Monthly Fee', value: `₹${student.monthly_fee || 800}` },
                    { label: 'Status', value: student.status },
                    { label: 'Qualification', value: student.educational_qualification },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-[#27272a] last:border-0">
                      <span className="text-sm text-[#a1a1aa]">{item.label}</span>
                      <span className="text-sm text-white font-medium capitalize">{item.value || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Fees Tab */}
        {tab === 'fees' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <StatCard icon={CreditCard} label="Total Paid" value={`₹${fees.total_paid}`} accent="#10b981" />
              <StatCard icon={AlertCircle} label="Total Due" value={`₹${fees.total_due}`} accent={fees.total_due > 0 ? '#ef4444' : '#10b981'} />
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="fees-table">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Fee History</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      <th className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">Month</th>
                      <th className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">Type</th>
                      <th className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">Amount</th>
                      <th className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">Status</th>
                      <th className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fees.fees.length === 0 ? (
                      <tr><td colSpan={5} className="p-8 text-center text-[#52525b]">No fee records found</td></tr>
                    ) : (
                      fees.fees.map((f, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-white">{f.month} {f.year}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] capitalize">{f.type}</td>
                          <td className="p-4 text-sm text-white font-medium">₹{f.amount}</td>
                          <td className="p-4">
                            <Badge className={f.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30'}>
                              {f.status === 'paid' ? <CheckCircle className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                              {f.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            {f.status === 'paid' && (
                              <Button size="sm" variant="ghost" onClick={() => viewReceipt(f)}
                                className="text-[#d4af37] hover:bg-[#d4af37]/10 text-xs" data-testid={`receipt-${f.month}-${f.year}`}>
                                <Printer className="w-3 h-3 mr-1" /> Receipt
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Attendance Tab */}
        {tab === 'attendance' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              <StatCard icon={Calendar} label="Total Days" value={attendance.total_days} accent="#d4af37" />
              <StatCard icon={CheckCircle} label="Present" value={attendance.present_days} accent="#10b981" />
              <StatCard icon={Clock} label="Late" value={attendance.late_days || 0} accent="#f59e0b" />
              <StatCard icon={XCircle} label="Absent" value={attendance.absent_days || 0} accent="#ef4444" />
              <StatCard icon={AlertCircle} label="Leave" value={attendance.leave_days || 0} accent="#8b5cf6" />
            </div>
            {/* Attendance percentage bar */}
            <div className="p-6 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid="attendance-progress">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-[#a1a1aa]">Attendance Rate</span>
                <span className="text-lg font-bold text-[#d4af37]">{attendance.percentage}%</span>
              </div>
              <div className="w-full h-3 bg-[#27272a] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#d4af37] to-[#facc15] rounded-full transition-all duration-500"
                  style={{ width: `${attendance.percentage}%` }} />
              </div>
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="attendance-table">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Attendance Records</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      <th className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">Date</th>
                      <th className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.records.length === 0 ? (
                      <tr><td colSpan={2} className="p-8 text-center text-[#52525b]">No attendance records</td></tr>
                    ) : (
                      attendance.records.map((r, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-white">{r.date}</td>
                          <td className="p-4">
                            <Badge className={r.status === 'present' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : r.status === 'late' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : r.status === 'leave' ? 'bg-violet-500/10 text-violet-400 border-violet-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30'}>
                              {r.status === 'present' ? <CheckCircle className="w-3 h-3 mr-1" /> : r.status === 'late' ? <Clock className="w-3 h-3 mr-1" /> : r.status === 'leave' ? <AlertCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                              {r.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Daily Mock Test Tab */}
        {tab === 'mocktest' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="mocktest-tab">
            {/* Past attempts summary */}
            {myAttempts.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-4 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase">Tests Taken</p><p className="text-2xl font-bold text-white mt-1">{myAttempts.length}</p></div>
                <div className="p-4 rounded-lg bg-[#18181b] border border-emerald-500/30"><p className="text-xs text-[#a1a1aa] uppercase">Best Score</p><p className="text-2xl font-bold text-emerald-400 mt-1">{Math.max(...myAttempts.map(a => a.percentage))}%</p></div>
                <div className="p-4 rounded-lg bg-[#18181b] border border-[#d4af37]/30"><p className="text-xs text-[#a1a1aa] uppercase">Avg Score</p><p className="text-2xl font-bold text-[#d4af37] mt-1">{Math.round(myAttempts.reduce((s, a) => s + a.percentage, 0) / myAttempts.length)}%</p></div>
                <div className="p-4 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase">Total Right</p><p className="text-2xl font-bold text-emerald-400 mt-1">{myAttempts.reduce((s, a) => s + a.correct, 0)}</p></div>
              </div>
            )}

            {/* Today's quiz */}
            {!todayQuiz ? (
              <div className="p-8 rounded-lg bg-[#18181b] border border-[#27272a] text-center">
                <Target className="w-10 h-10 text-[#52525b] mx-auto mb-3" />
                <p className="text-white/70">No mock test published yet for today.</p>
                <p className="text-xs text-[#52525b] mt-2">Check back later — your admin posts a new test daily.</p>
              </div>
            ) : (
              <>
                {/* Check if already attempted today */}
                {myAttempts.some(a => a.quiz_id === todayQuiz.id) && quizStage === 'intro' ? (
                  <div className="p-6 rounded-lg bg-emerald-500/5 border border-emerald-500/20" data-testid="already-attempted">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                      <h3 className="text-base font-semibold text-white">You've already attempted today's test!</h3>
                    </div>
                    {(() => {
                      const a = myAttempts.find(x => x.quiz_id === todayQuiz.id);
                      return (
                        <div className="grid grid-cols-4 gap-3 mb-3">
                          <div className="p-3 rounded bg-[#18181b]"><p className="text-2xl font-bold text-emerald-400">{a.correct}</p><p className="text-[10px] text-[#a1a1aa] uppercase">Correct</p></div>
                          <div className="p-3 rounded bg-[#18181b]"><p className="text-2xl font-bold text-red-400">{a.wrong}</p><p className="text-[10px] text-[#a1a1aa] uppercase">Wrong</p></div>
                          <div className="p-3 rounded bg-[#18181b]"><p className="text-2xl font-bold text-[#d4af37]">{a.score}</p><p className="text-[10px] text-[#a1a1aa] uppercase">Marks</p></div>
                          <div className="p-3 rounded bg-[#18181b]"><p className="text-2xl font-bold text-white">{a.percentage}%</p><p className="text-[10px] text-[#a1a1aa] uppercase">Score</p></div>
                        </div>
                      );
                    })()}
                  </div>
                ) : quizStage === 'intro' ? (
                  <div className="p-6 rounded-lg bg-gradient-to-br from-[#18181b] to-[#0f0f12] border border-[#d4af37]/30">
                    <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-[#d4af37]" /><h3 className="text-lg font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{todayQuiz.title}</h3></div>
                    <div className="grid grid-cols-4 gap-3 my-5">
                      <div className="text-center"><p className="text-xl font-bold text-[#d4af37]">{todayQuiz.total_questions}</p><p className="text-[10px] text-[#a1a1aa]">Questions</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-emerald-400">+1</p><p className="text-[10px] text-[#a1a1aa]">Correct</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-red-400">-1/3</p><p className="text-[10px] text-[#a1a1aa]">Wrong</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-white/70">0</p><p className="text-[10px] text-[#a1a1aa]">Skipped</p></div>
                    </div>
                    <Button onClick={() => { setQuizStage('quiz'); setQuizCurrentQ(0); }} className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="start-quiz-student">
                      Start Test <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ) : null}

                {quizStage === 'quiz' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center"><p className="text-sm text-white/70">Q{quizCurrentQ + 1} of {todayQuiz.total_questions}</p><p className="text-xs text-emerald-400">{quizAnswers.filter(a => a !== null).length} answered</p></div>
                    <div className="w-full h-1.5 bg-[#27272a] rounded-full overflow-hidden"><div className="h-full bg-[#d4af37]" style={{ width: `${((quizCurrentQ + 1) / todayQuiz.total_questions) * 100}%` }} /></div>
                    <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                      <p className="text-base font-semibold text-white mb-4 whitespace-pre-wrap">{todayQuiz.questions[quizCurrentQ].question}</p>
                      <div className="space-y-2">
                        {todayQuiz.questions[quizCurrentQ].options.map((opt, i) => (
                          <button key={i} onClick={() => setQuizAnswer(quizCurrentQ, i)}
                            className={`w-full text-left px-3 py-2.5 rounded border text-sm flex items-center gap-3 ${quizAnswers[quizCurrentQ] === i ? 'border-[#d4af37] bg-[#d4af37]/10 text-white' : 'border-[#27272a] text-[#a1a1aa] hover:border-[#3f3f46]'}`}>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${quizAnswers[quizCurrentQ] === i ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#27272a] text-[#a1a1aa]'}`}>{String.fromCharCode(65+i)}</span>
                            {opt}
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-between mt-5 gap-2">
                        <Button variant="outline" onClick={() => setQuizAnswer(quizCurrentQ, null)} className="border-[#3f3f46] text-[#a1a1aa] bg-transparent">Skip</Button>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setQuizCurrentQ(Math.max(0, quizCurrentQ - 1))} disabled={quizCurrentQ === 0} className="border-[#3f3f46] text-white bg-transparent">← Prev</Button>
                          {quizCurrentQ < todayQuiz.total_questions - 1 ? (
                            <Button onClick={() => setQuizCurrentQ(quizCurrentQ + 1)} className="bg-[#d4af37] text-[#18181b]">Next →</Button>
                          ) : (
                            <Button onClick={submitQuiz} disabled={quizLoading} className="bg-emerald-600 text-white" data-testid="submit-quiz-student">{quizLoading ? 'Submitting...' : 'Submit'}</Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {quizStage === 'result' && quizResult && (
                  <div className="space-y-4">
                    <div className="p-6 rounded-lg bg-gradient-to-br from-[#18181b] to-[#0f0f12] border border-[#d4af37]/30 text-center" data-testid="quiz-result-student">
                      <Trophy className="w-10 h-10 text-[#d4af37] mx-auto mb-3" />
                      <h3 className="text-xl font-bold text-white mb-4">Test Submitted!</h3>
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div className="p-3 rounded bg-[#0f0f12]"><p className="text-2xl font-bold text-emerald-400">{quizResult.correct}</p><p className="text-[10px] text-[#a1a1aa] uppercase">Correct</p></div>
                        <div className="p-3 rounded bg-[#0f0f12]"><p className="text-2xl font-bold text-red-400">{quizResult.wrong}</p><p className="text-[10px] text-[#a1a1aa] uppercase">Wrong</p></div>
                        <div className="p-3 rounded bg-[#0f0f12]"><p className="text-2xl font-bold text-white/70">{quizResult.skipped}</p><p className="text-[10px] text-[#a1a1aa] uppercase">Skipped</p></div>
                        <div className="p-3 rounded bg-[#0f0f12]"><p className="text-2xl font-bold text-[#d4af37]">{quizResult.percentage}%</p><p className="text-[10px] text-[#a1a1aa] uppercase">Score</p></div>
                      </div>
                      <div className="p-4 rounded bg-[#d4af37]/10 border border-[#d4af37]/30 inline-block">
                        <p className="text-2xl font-bold text-[#d4af37]">{quizResult.score} / {quizResult.max_score || quizResult.total} marks</p>
                        <p className="text-[10px] text-[#a1a1aa] mt-1">PSC marking: +{quizResult.positive_marks || 1} correct · −{quizResult.negative_marks || 0.33} wrong</p>
                      </div>
                    </div>

                    {/* AI Weak Area Analysis */}
                    <div className="p-5 rounded-lg bg-gradient-to-br from-purple-500/10 to-[#0f0f12] border border-purple-500/30" data-testid="ai-analysis-card">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2"><Brain className="w-5 h-5 text-purple-300" /><h3 className="text-base font-semibold text-white">AI Performance Analysis</h3></div>
                        {aiLoading && <span className="text-xs text-purple-300 animate-pulse">Analysing…</span>}
                      </div>
                      {!aiAnalysis && !aiLoading && (
                        <Button onClick={() => fetchAiAnalysis(quizResult.attempt_id)} size="sm" className="bg-purple-500/20 text-purple-200 hover:bg-purple-500/30">
                          Generate Analysis
                        </Button>
                      )}
                      {aiAnalysis && (
                        <div className="space-y-3">
                          {aiAnalysis.recommendations && (
                            <p className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap" data-testid="ai-recommendations">{aiAnalysis.recommendations}</p>
                          )}
                          <div className="grid grid-cols-2 gap-3">
                            {Number.isFinite(aiAnalysis.predicted_psc_score) && (
                              <div className="p-3 rounded bg-[#0f0f12] border border-[#27272a]"><p className="text-[10px] text-[#a1a1aa] uppercase">Predicted PSC Score</p><p className="text-xl font-bold text-emerald-300">{aiAnalysis.predicted_psc_score}/100</p></div>
                            )}
                            {Number.isFinite(aiAnalysis.readiness_percent) && (
                              <div className="p-3 rounded bg-[#0f0f12] border border-[#27272a]"><p className="text-[10px] text-[#a1a1aa] uppercase">Readiness</p><p className="text-xl font-bold text-[#d4af37]">{aiAnalysis.readiness_percent}%</p></div>
                            )}
                          </div>
                          {aiAnalysis.weak_subjects?.length > 0 && (
                            <div>
                              <p className="text-xs uppercase text-red-300 mb-1">Weak Areas — Revise these first</p>
                              <div className="flex flex-wrap gap-2">
                                {aiAnalysis.weak_subjects.map((s, i) => (<span key={i} className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-200 border border-red-500/30">{s.name} ({s.accuracy}%)</span>))}
                              </div>
                            </div>
                          )}
                          {aiAnalysis.strong_subjects?.length > 0 && (
                            <div>
                              <p className="text-xs uppercase text-emerald-300 mb-1">Strong Areas</p>
                              <div className="flex flex-wrap gap-2">
                                {aiAnalysis.strong_subjects.map((s, i) => (<span key={i} className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">{s.name} ({s.accuracy}%)</span>))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* History */}
            {myAttempts.length > 0 && (
              <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden">
                <div className="p-4 border-b border-[#27272a]"><h3 className="text-base font-semibold text-white">Your Past Attempts</h3></div>
                <div className="overflow-x-auto"><table className="w-full">
                  <thead><tr className="border-b border-[#27272a]">
                    {['Date', 'Title', 'Score', '%', 'Right/Wrong/Skip'].map(h => (<th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {myAttempts.map((a, i) => (
                      <tr key={i} className="border-b border-[#27272a] last:border-0">
                        <td className="p-3 text-sm text-[#a1a1aa]">{a.quiz_date}</td>
                        <td className="p-3 text-sm text-white">{a.quiz_title}</td>
                        <td className="p-3 text-sm text-[#d4af37] font-bold">{a.score}/{a.total}</td>
                        <td className={`p-3 text-sm font-bold ${a.percentage >= 60 ? 'text-emerald-400' : a.percentage >= 33 ? 'text-amber-400' : 'text-red-400'}`}>{a.percentage}%</td>
                        <td className="p-3 text-xs text-[#a1a1aa]"><span className="text-emerald-400">{a.correct}</span>/<span className="text-red-400">{a.wrong}</span>/<span className="text-white/50">{a.skipped}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            )}
          </motion.div>
        )}

        {/* 100-Day Plan Tab */}
        {tab === 'plan100' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="plan100-tab">
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>100-Day Study Plan</h2>
              <p className="text-sm text-[#a1a1aa] mt-1">
                {studyPlan?.unlocked_until ? `${studyPlan.unlocked_until} days unlocked · Current plan: ${studyPlan.current_tier || 'free'}` : 'Subscribe to unlock all 100 days.'}
              </p>
            </div>
            {!studyPlan ? (
              <div className="p-8 rounded-lg bg-[#18181b] border border-[#27272a] text-center"><p className="text-sm text-[#a1a1aa]">Loading…</p></div>
            ) : (
              <>
                <div className="grid grid-cols-5 md:grid-cols-10 gap-2" data-testid="plan100-grid">
                  {studyPlan.days.map(d => {
                    const totalContent = d.notes_count + d.quizzes_count;
                    const allLocked = d.locked || (totalContent === 0);
                    return (
                      <button key={d.day} disabled={d.locked || totalContent === 0}
                        onClick={() => {
                          // jump to notes tab filtered by day
                          if (d.notes_count > 0) setTab('notes');
                          else if (d.quizzes_count > 0) setTab('mocktest');
                        }}
                        className={`relative aspect-square rounded-lg flex flex-col items-center justify-center border transition-all
                          ${d.locked ? 'bg-[#0f0f10] border-[#27272a] text-[#52525b] cursor-not-allowed' :
                            totalContent === 0 ? 'bg-[#18181b] border-[#27272a] text-[#52525b] cursor-default' :
                              'bg-emerald-500/10 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/20'
                          }`} data-testid={`plan100-day-${d.day}`}>
                        {d.locked && <Lock className="absolute top-1 right-1 w-2.5 h-2.5" />}
                        <span className={`text-xs font-bold ${d.locked ? '' : 'text-white'}`}>{d.day}</span>
                        {totalContent > 0 && !allLocked && (
                          <span className="text-[8px] text-emerald-300 mt-0.5">
                            {d.notes_count > 0 && `${d.notes_count}N`}{d.notes_count > 0 && d.quizzes_count > 0 && '·'}{d.quizzes_count > 0 && `${d.quizzes_count}Q`}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <p className="text-[10px] uppercase text-emerald-300">Unlocked</p>
                    <p className="text-2xl font-bold text-emerald-200">{studyPlan.days.filter(d => !d.locked).length} <span className="text-sm font-normal">/ 100</span></p>
                  </div>
                  <div className="p-4 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/30">
                    <p className="text-[10px] uppercase text-[#d4af37]">Total Notes</p>
                    <p className="text-2xl font-bold text-[#facc15]">{studyPlan.days.reduce((s, d) => s + d.notes_count, 0)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                    <p className="text-[10px] uppercase text-purple-300">Total Quizzes</p>
                    <p className="text-2xl font-bold text-purple-200">{studyPlan.days.reduce((s, d) => s + d.quizzes_count, 0)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-[#a1a1aa] mt-2">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-500/40 inline-block" /> Unlocked & has content</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#18181b] border border-[#27272a] inline-block" /> Empty</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#0f0f10] border border-[#27272a] inline-block" /> Locked</span>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* Referrals Tab */}
        {tab === 'referrals' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="referrals-tab">
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Refer & Earn Premium Days</h2>
              <p className="text-sm text-[#a1a1aa] mt-1">Invite friends to SIGNATURE PSC. Each successful subscription earns you bonus days.</p>
            </div>
            {!referrals ? (
              <div className="p-8 rounded-lg bg-[#18181b] border border-[#27272a] text-center"><p className="text-sm text-[#a1a1aa]">Loading…</p></div>
            ) : (
              <>
                <div className="p-5 rounded-lg bg-gradient-to-br from-[#d4af37]/15 to-[#facc15]/5 border border-[#d4af37]/30">
                  <p className="text-xs uppercase text-[#d4af37]">Your Referral Code</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <code className="text-2xl font-bold text-white tracking-widest" data-testid="referral-code">{referrals.referral_code}</code>
                    <Button onClick={copyReferralLink} variant="ghost" size="sm" className="text-[#d4af37] hover:bg-[#d4af37]/10" data-testid="referral-copy">
                      <Copy className="w-3 h-3 mr-1" /> Copy Link
                    </Button>
                    <Button onClick={shareReferralWhatsApp} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="referral-whatsapp">
                      <Share2 className="w-3 h-3 mr-1" /> Share on WhatsApp
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#a1a1aa] mt-2 break-all">{referrals.share_link}</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-4 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-[10px] uppercase text-[#a1a1aa]">Confirmed</p><p className="text-2xl font-bold text-emerald-400" data-testid="ref-confirmed">{referrals.confirmed_referrals}</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-[10px] uppercase text-[#a1a1aa]">Pending</p><p className="text-2xl font-bold text-amber-400">{referrals.pending_referrals}</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-[10px] uppercase text-[#a1a1aa]">Total Invites</p><p className="text-2xl font-bold text-white">{referrals.total_referrals}</p></div>
                  <div className="p-4 rounded-lg bg-[#d4af37]/15 border border-[#d4af37]/30"><p className="text-[10px] uppercase text-[#d4af37]">Bonus Days</p><p className="text-2xl font-bold text-[#facc15]" data-testid="ref-bonus">{referrals.bonus_days_earned}</p></div>
                </div>
                <div className="p-4 rounded-lg bg-[#18181b] border border-[#27272a]">
                  <p className="text-xs uppercase text-[#a1a1aa] mb-2">Milestone Rewards</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {referrals.milestones.map(m => (
                      <div key={m.count} className={`p-3 rounded text-center border ${referrals.confirmed_referrals >= m.count ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-[#27272a]/30 border-[#27272a]'}`}>
                        <p className="text-[10px] uppercase text-[#a1a1aa]">{m.count} referral{m.count > 1 ? 's' : ''}</p>
                        <p className="text-base font-bold text-white">+{m.bonus_days} days</p>
                        {referrals.confirmed_referrals >= m.count && <CheckCircle className="w-3 h-3 text-emerald-400 inline-block mt-1" />}
                      </div>
                    ))}
                  </div>
                </div>
                {referrals.referred_list.length > 0 && (
                  <div className="rounded-lg bg-[#18181b] border border-[#27272a] divide-y divide-[#27272a]/50">
                    {referrals.referred_list.map((r, i) => (
                      <div key={i} className="p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm text-white">{r.name}</p>
                          <p className="text-[10px] text-[#a1a1aa]">{(r.created_at || '').split('T')[0]}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold ${r.referral_credited ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                          {r.referral_credited ? 'Subscribed' : 'Pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* Study Notes Tab */}
        {tab === 'notes' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="notes-tab">
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Study Notes</h2>
              <p className="text-sm text-[#a1a1aa] mt-1">PDF & online notes. Sorted by Day for the 100-day curriculum.</p>
            </div>
            {notes.length === 0 ? (
              <div className="p-8 rounded-lg bg-[#18181b] border border-[#27272a] text-center">
                <BookOpen className="w-10 h-10 text-[#52525b] mx-auto mb-3" />
                <p className="text-sm text-[#a1a1aa]">No notes published yet. Check back soon.</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {[...notes].sort((a,b) => (a.day_number || 999) - (b.day_number || 999)).map(n => (
                  <div key={n.id} className={`p-4 rounded-lg border ${n.locked ? 'bg-[#18181b] border-[#27272a] opacity-70' : 'bg-[#18181b] border-[#27272a] hover:border-[#d4af37]/30'} transition-all`} data-testid={`note-${n.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {n.day_number && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-bold uppercase">Day {n.day_number}</span>}
                          <span className="text-[10px] px-2 py-0.5 rounded bg-[#d4af37]/15 text-[#d4af37] uppercase">{n.note_type}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-[#27272a] text-[#a1a1aa]">{n.subject || 'General'}</span>
                          {n.locked && <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-300 uppercase font-bold flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> {n.required_tier_name || 'Premium'}</span>}
                        </div>
                        <p className="text-sm font-semibold text-white">{n.title}</p>
                        {n.description && <p className="text-xs text-[#a1a1aa] mt-1">{n.description}</p>}
                      </div>
                      <div className="flex-shrink-0">
                        {n.locked ? (
                          <Button onClick={() => setTab('myplan')} size="sm" className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] text-xs h-8" data-testid={`note-upgrade-${n.id}`}>
                            Upgrade
                          </Button>
                        ) : n.note_type === 'pdf' && n.pdf_path ? (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${n.pdf_path}`} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-xs font-semibold" data-testid={`note-open-${n.id}`}>
                            <Download className="w-3.5 h-3.5" /> Open PDF
                          </a>
                        ) : (
                          <Button onClick={() => setOpenNote(n)} size="sm" className="bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-xs h-8" data-testid={`note-read-${n.id}`}>
                            <FileText className="w-3.5 h-3.5 mr-1" /> Read
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* My Plan Tab */}
        {tab === 'myplan' && subscription && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="myplan-tab">
            {/* Status banner */}
            <div className={`p-5 rounded-lg border ${
              subscription.state === 'active' ? 'bg-emerald-500/10 border-emerald-500/30' :
              subscription.state === 'trial' ? 'bg-[#d4af37]/10 border-[#d4af37]/30' :
              subscription.state === 'awaiting_approval' ? 'bg-amber-500/10 border-amber-500/30' :
              'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/60 mb-1">Current Status</p>
                  <p className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {subscription.state === 'active' ? `✓ Active Subscription · ${subscription.days_left} days left` :
                     subscription.state === 'trial' ? `🎁 Free Trial · ${subscription.days_left} days left` :
                     subscription.state === 'awaiting_approval' ? '⏳ Payment Awaiting Admin Approval' :
                     subscription.state === 'trial_expired' ? '⚠️ Trial Expired — Pay to Continue' :
                     subscription.state === 'expired' ? '⚠️ Subscription Expired' :
                     subscription.state}
                  </p>
                  <p className="text-sm text-white/60 mt-1">Plan: <strong className="text-white">{plansInfo?.plans?.[subscription.plan]?.name}</strong> · ₹{plansInfo?.plans?.[subscription.plan]?.price}/100 days</p>
                </div>
                {subscription.state === 'awaiting_approval' && subscription.utr_number && (
                  <div className="text-right">
                    <p className="text-[10px] text-white/50 uppercase">UTR submitted</p>
                    <p className="text-sm text-amber-400 font-mono">{subscription.utr_number}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pay form — show if trial active, trial expired, or expired */}
            {(['trial', 'trial_expired', 'expired', 'pending'].includes(subscription.state)) && (
              <>
                {/* INSTANT — Razorpay one-click */}
                <div className="p-5 rounded-lg bg-gradient-to-br from-[#d4af37]/15 to-[#facc15]/5 border border-[#d4af37]/40" data-testid="razorpay-card">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#d4af37] font-bold">Recommended · Instant Activation</p>
                      <p className="text-base font-semibold text-white mt-1">Pay ₹{(plansInfo?.plans?.[payForm.plan]?.price || 0) * payForm.months} securely via Card / UPI / Wallet</p>
                      <p className="text-[11px] text-[#a1a1aa] mt-0.5">Plan auto-activated within seconds — no admin approval wait.</p>
                    </div>
                    <Button onClick={() => payWithRazorpay(payForm.plan, payForm.months)} disabled={rzpLoading}
                      className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-bold px-6 h-11" data-testid="razorpay-pay-btn">
                      {rzpLoading ? 'Opening…' : `Pay ₹${(plansInfo?.plans?.[payForm.plan]?.price || 0) * payForm.months} Now`}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-[#52525b]">
                  <div className="flex-1 h-px bg-[#27272a]" /> OR pay manually via GPay below <div className="flex-1 h-px bg-[#27272a]" />
                </div>
              <div className="grid md:grid-cols-2 gap-5">
                {/* Left: GPay QR + UPI ID */}
                <div className="p-5 rounded-lg bg-white text-[#1a2e1c]" data-testid="upi-payment-card">
                  <p className="text-xs uppercase tracking-wider text-[#5a6b5c] font-semibold">Scan to Pay via UPI</p>
                  <p className="text-base font-bold mt-1">{plansInfo?.upi_name}</p>
                  {plansInfo?.upi_qr_image && (
                    <img src={plansInfo.upi_qr_image} alt="Google Pay QR Code" className="w-full max-w-[280px] mx-auto my-3" data-testid="upi-qr-img" />
                  )}
                  <div className="p-3 rounded bg-[#f5f5f0] border border-[#e8e0cc]">
                    <p className="text-[10px] uppercase tracking-wider text-[#5a6b5c]">UPI ID</p>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-mono font-bold text-[#1a2e1c]">{plansInfo?.upi_id}</p>
                      <button onClick={() => { navigator.clipboard.writeText(plansInfo?.upi_id || ''); toast.success('Copied!'); }} className="text-xs text-[#a07c2a] hover:text-[#c8a84e] underline" data-testid="copy-upi">Copy</button>
                    </div>
                    <p className="text-[10px] text-[#5a6b5c] mt-2">Phone: +91 {plansInfo?.upi_phone}</p>
                  </div>
                  <p className="text-[11px] text-[#5a6b5c] mt-3 leading-relaxed">Open Google Pay / PhonePe / Paytm → Scan QR → Pay <strong>₹{plansInfo?.plans?.[payForm.plan]?.price * payForm.months || 0}</strong> → Copy the UPI transaction reference (UTR) → Paste below.</p>
                </div>
                {/* Right: submit form */}
                <form onSubmit={submitPayment} className="p-5 rounded-lg bg-[#18181b] border border-[#27272a] space-y-3">
                  <p className="text-base font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Submit Payment</p>
                  <div>
                    <Label className="text-[#a1a1aa] text-xs">Plan</Label>
                    <Select value={payForm.plan} onValueChange={v => setPayForm({ ...payForm, plan: v })}>
                      <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="pay-plan"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-[#27272a]">
                        {plansInfo?.plans && Object.entries(plansInfo.plans).map(([k, p]) => (
                          <SelectItem key={k} value={k} className="text-white hover:bg-[#27272a]">{p.name} — ₹{p.price}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#a1a1aa] text-xs">Cycles (1 = 100 days, 2 = 200 days)</Label>
                    <Input type="number" min="1" max="6" value={payForm.months} onChange={e => setPayForm({ ...payForm, months: parseInt(e.target.value) || 1 })} className="bg-[#09090b] border-[#27272a] text-white" />
                  </div>
                  <div className="p-3 rounded bg-[#d4af37]/10 border border-[#d4af37]/30 text-center">
                    <p className="text-[10px] text-[#a1a1aa] uppercase">Amount to Pay</p>
                    <p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{(plansInfo?.plans?.[payForm.plan]?.price || 0) * payForm.months}</p>
                  </div>
                  <div>
                    <Label className="text-[#a1a1aa] text-xs">UTR / Transaction ID *</Label>
                    <Input value={payForm.utr_number} onChange={e => setPayForm({ ...payForm, utr_number: e.target.value })} placeholder="e.g. 123456789012" required
                      className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="pay-utr" />
                    <p className="text-[10px] text-[#52525b] mt-1">Find UTR in your GPay/PhonePe transaction details</p>
                  </div>
                  <div>
                    <Label className="text-[#a1a1aa] text-xs">Notes (optional)</Label>
                    <Input value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
                      className="bg-[#09090b] border-[#27272a] text-white" />
                  </div>
                  <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="pay-submit">
                    Submit for Approval
                  </Button>
                </form>
              </div>
              </>
            )}

            {/* Plan features */}
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] p-5">
              <p className="text-sm font-semibold text-white mb-3">What's included in your plan</p>
              <ul className="space-y-1.5">
                {plansInfo?.plans?.[subscription.plan]?.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-white/70">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </div>

      {/* HTML Note Viewer */}
      <Dialog open={!!openNote} onOpenChange={(o) => !o && setOpenNote(null)}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="note-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 flex-wrap text-base sm:text-lg pr-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {openNote?.day_number && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-bold uppercase">Day {openNote.day_number}</span>}
              <span className="break-words">{openNote?.title}</span>
            </DialogTitle>
          </DialogHeader>
          {openNote?.description && <p className="text-sm text-[#a1a1aa] mb-3">{openNote.description}</p>}
          <div className="note-html-body text-white/90 text-sm sm:text-base leading-relaxed"
               dangerouslySetInnerHTML={{ __html: openNote?.html_content || '' }} />
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Fee Receipt</DialogTitle>
          </DialogHeader>
          {receipt && (
            <>
              <div ref={receiptRef} className="bg-white text-black p-6 rounded-md">
                <div className="header text-center border-b-2 border-[#d4af37] pb-3 mb-4">
                  <h1 className="text-lg font-bold text-black">{receipt.centre_name}</h1>
                  <p className="text-xs text-gray-600">{receipt.centre_address}</p>
                  <p className="text-xs text-gray-600">Phone: {receipt.centre_phone}</p>
                </div>
                <p className="text-right text-xs text-gray-500 mb-3">Receipt No: {receipt.receipt_no}</p>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Student ID</td><td className="py-2 text-right text-black">{receipt.student_id}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Student Name</td><td className="py-2 text-right text-black">{receipt.student_name}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Batch</td><td className="py-2 text-right capitalize text-black">{receipt.batch}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Month / Year</td><td className="py-2 text-right text-black">{receipt.month} {receipt.year}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Fee Type</td><td className="py-2 text-right capitalize text-black">{receipt.type}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Payment Method</td><td className="py-2 text-right capitalize text-black">{receipt.payment_method}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Paid Date</td><td className="py-2 text-right text-black">{receipt.paid_date?.split('T')[0]}</td></tr>
                    <tr className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">Collected By</td><td className="py-2 text-right text-black">{receipt.collected_by}</td></tr>
                  </tbody>
                </table>
                <div className="text-center py-4 mt-3 bg-gray-50 border border-[#d4af37] rounded">
                  <span className="text-2xl font-bold text-black">₹{receipt.amount}</span>
                  <p className="text-xs text-gray-500 mt-1">Amount Paid</p>
                </div>
                <p className="text-right mt-6 text-xs italic text-green-700">Paid - Verified</p>
              </div>
              <div className="flex gap-2 mt-3">
                <Button onClick={handleDownloadPDF} className="flex-1 bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="download-receipt-pdf">
                  <FileDown className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button onClick={handleSharePDF} variant="outline" className="flex-1 border-[#3f3f46] text-white hover:bg-[#27272a]" data-testid="share-receipt-pdf">
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a] hover:border-[#d4af37]/20 transition-all" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">{label}</p>
          <p className="text-2xl font-bold" style={{ color: accent, fontFamily: "'Playfair Display', serif" }}>{value}</p>
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}
